import { cloneDeep, forOwn, get, isString, isObject, set } from 'lodash';
import { ValidationError } from 'joi';
import { DomainError } from 'src/models/DomainError';
import { ExternalSource } from 'src/actions/integrations/integrationSources';
import { WebhookIncoming } from 'src/actions/integrations/webhook';

const SECRET_PLACEHOLDER = 'XXXXXXXXX';

export const FIELD_LABEL_DELIMITER = '\x01';

/**
 * Translates Joi error messages into the { <attr> : <message> } map that can be used with Forms
 *
 * @param error [Joi.ValidationError]
 * @param labelDelimiter [String] - single character string that delimits field label/name in the
 * error message generated by Joi
 */
export function translateJoiErrors(
  error: ValidationError,
  labelDelimiter: string = FIELD_LABEL_DELIMITER
): DomainError[] {
  if (!error?.details.length) return [];

  // Helper function to extract all errors recursively
  const extractAllErrors = (details: any[]): any[] => {
    const allErrors: any[] = [];

    details.forEach(errorDetail => {
      // Add the current error
      allErrors.push(errorDetail);

      // Check if there are nested errors in context.details
      if (errorDetail.context?.details && Array.isArray(errorDetail.context.details)) {
        allErrors.push(...extractAllErrors(errorDetail.context.details));
      }
    });

    return allErrors;
  };

  // Extract all errors (top-level and nested)
  const allErrors = extractAllErrors(error.details);

  return allErrors.map(errorDetails => {
    const regex = `${labelDelimiter}(.*?)${labelDelimiter}`;
    const path = errorDetails.path.join('.');

    const matches = errorDetails.message.match(regex);
    const fieldName = matches ? matches[1] || 'The value' : '';
    const message = errorDetails.message.replace(new RegExp(regex), '').trim();
    const errorMessage = `${fieldName.trim()} ${message}`.trim();

    return { code: 'invalid', attr: path, detail: errorMessage };
  });
}

/**
 * Add "stand in" placeholders to the secret fields that have no value AND appear in either
 * "hasCustomHeaders" or "hasSecrets" (i.e. we do have values for those secrets in our database but
 * don't show them in the UI)
 *
 * @param dataItem
 */
export function addSecrets<T extends Partial<ExternalSource>>(dataItem: T): T {
  const clone = cloneDeep(dataItem);
  const secrets = clone.secrets?.hasSecrets || [];
  const headers = clone.secrets?.hasCustomHeaders || [];

  secrets.forEach(secretPath => {
    const fullPath = `secrets.${secretPath}`;
    if (get(clone, fullPath) !== undefined) return;
    set(clone, fullPath, SECRET_PLACEHOLDER);
  });
  if (headers.length) {
    const existingHeaders = get(clone, 'secrets.headers', {});
    const headerPlaceholders = headers.reduce((acc, headerName) => {
      acc[headerName] = SECRET_PLACEHOLDER;
      return acc;
    }, {});
    set(clone, 'secrets.headers', { ...existingHeaders, ...headerPlaceholders });
  }

  return clone;
}

export function addWebhookSecrets<T extends WebhookIncoming>(dataItem: T): T {
  const clone = cloneDeep(dataItem);
  const secrets = clone.hasCredentials ? ['credentials.username', 'credentials.password', 'credentials.realm'] : [];
  const headers = clone.headersKeys || [];

  secrets.forEach(secretPath => {
    if (get(clone, secretPath) !== undefined) return;
    set(clone, secretPath, SECRET_PLACEHOLDER);
  });
  if (headers.length) {
    const existingHeaders = get(clone, 'headers', {});
    const headerPlaceholders = headers.reduce((acc, headerName) => {
      acc[headerName] = SECRET_PLACEHOLDER;
      return acc;
    }, {});
    set(clone, 'headers', { ...existingHeaders, ...headerPlaceholders });
  }

  return clone;
}

/**
 * We need to convert "empty" secrets to nulls in order to follow the convention on how SN treats
 * empty JSON elements. We consider a secret "empty" if its value is an empty string or a string that
 * contains only whitespace(s)
 *
 * @param dataItem
 */
export function processEmptySecrets<T extends Partial<ExternalSource>>(dataItem: T): T {
  if (!dataItem.secrets) return dataItem;

  const traverse = <N extends object>(node: N): N => {
    forOwn(node, (value, key) => {
      if (isObject(value)) return traverse(value);
      if (isString(value) && value.trim() === '') set(node, key, null);
    });
    return node;
  };

  const clone = cloneDeep(dataItem);
  traverse(clone.secrets!);
  return clone;
}
